import { useLocation, useSearchParams } from "react-router-dom";

/** The param, exported so the layout, the page and the tests all name the same string. */
export const TV_PARAM = "board:tv";

/**
 * Presentation mode: the pipeline as a wall display.
 *
 * **The URL is the carrier, not the Fullscreen API.** A TV bookmarks a filtered, presented
 * board and reloads into it, and browsers refuse fullscreen without a user gesture — so
 * fullscreen alone could never survive a reload. The two are deliberately decoupled:
 * leaving browser fullscreen does not leave TV mode; the ✕ and Esc do.
 *
 * A sibling of `usePipelineScope` rather than part of it, because `AppLayout` needs the
 * answer to hide its chrome and must not pay for the year window's query to get it.
 *
 * The pipeline route only, **either view** (amended 2026-08-25 — the list gets presentation
 * mode too): a `board:tv` param carried onto the client registry by a stray link must not
 * strip that screen's navigation. While a detail modal is open the surface tree still
 * renders against the pipeline's own location — see `App.tsx` — so the chrome stays hidden
 * underneath the modal rather than flashing back in.
 */
export function useTvMode(): { tv: boolean; setTv: (next: boolean) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const onPipeline = location.pathname === "/";
  const tv = onPipeline && searchParams.get(TV_PARAM) === "1";

  const setTv = (next: boolean): void => {
    // See useFilterParam — read the live URL, not react-router's render-time snapshot.
    const params = new URLSearchParams(window.location.search);
    // Deleted when off, like every other param at its default: a board that is not being
    // presented has a clean URL.
    if (next) params.set(TV_PARAM, "1");
    else params.delete(TV_PARAM);
    setSearchParams(params, { replace: true });
  };

  return { tv, setTv };
}

/**
 * Ask the browser for fullscreen, and give up quietly if it refuses.
 *
 * It refuses without a user gesture, and it refuses outright in some embedded contexts —
 * neither is an error worth showing, because TV layout is already on by then and that is
 * the part the reader asked for.
 */
export function requestFullscreen(): void {
  const element = document.documentElement;
  if (typeof element.requestFullscreen !== "function") return;
  void element.requestFullscreen().catch(() => undefined);
}

/** Leave fullscreen if we are in it. Never throws for the same reasons as above. */
export function exitFullscreen(): void {
  if (!document.fullscreenElement || typeof document.exitFullscreen !== "function") return;
  void document.exitFullscreen().catch(() => undefined);
}
